const OPEN_STREET_MAP_ID = 'openstreetmap';
const openStreetMapRegistry = new WeakMap();
const openStreetMapOverlayRegistry = new WeakMap();

const getOpenStreetMapTileUrl = (tileX, tileY, zoom) => {
  const tileCount = 2 ** zoom;
  const x = ((tileX % tileCount) + tileCount) % tileCount;
  const subdomain = ['a', 'b', 'c'][Math.abs(x + tileY) % 3];
  return `https://${subdomain}.tile.openstreetmap.org/${zoom}/${x}/${tileY}.png`;
};

const createOpenStreetMapOverlay = (google, map) => {
  const overlay = new google.maps.OverlayView();
  let container = null;

  overlay.onAdd = () => {
    container = document.createElement('div');
    container.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none;';
    overlay.getPanes().overlayLayer.appendChild(container);
  };

  overlay.draw = () => {
    if (!container) return;

    const projection = overlay.getProjection();
    const bounds = map.getBounds();
    const zoom = map.getZoom();
    if (!projection || !bounds || !Number.isFinite(zoom)) return;

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const topRight = projection.fromLatLngToDivPixel(
      new google.maps.LatLng(northEast.lat(), southWest.lng())
    );
    const bottomLeft = projection.fromLatLngToDivPixel(
      new google.maps.LatLng(southWest.lat(), northEast.lng())
    );
    const tileSize = 256;
    const tileCount = 2 ** zoom;
    const minX = Math.floor(topRight.x / tileSize);
    const maxX = Math.floor(bottomLeft.x / tileSize);
    const minY = Math.floor(topRight.y / tileSize);
    const maxY = Math.floor(bottomLeft.y / tileSize);

    container.replaceChildren();

    for (let tileX = minX; tileX <= maxX; tileX += 1) {
      for (let tileY = minY; tileY <= maxY; tileY += 1) {
        if (tileY < 0 || tileY >= tileCount) continue;

        const tile = document.createElement('img');
        tile.src = getOpenStreetMapTileUrl(tileX, tileY, zoom);
        tile.alt = '';
        tile.draggable = false;
        tile.decoding = 'async';
        tile.style.cssText = [
          'position:absolute',
          `left:${tileX * tileSize - topRight.x}px`,
          `top:${tileY * tileSize - topRight.y}px`,
          `width:${tileSize}px`,
          `height:${tileSize}px`,
          'max-width:none',
          'user-select:none',
        ].join(';');
        container.appendChild(tile);
      }
    }
  };

  overlay.onRemove = () => {
    container?.remove();
    container = null;
  };

  return overlay;
};

export const registerOpenStreetMap = (google, map, { showControl = true, customControl = false } = {}) => {
  if (!google?.maps || !map) return;

  const osmMapType = new google.maps.ImageMapType({
    name: 'OpenStreetMap',
    alt: 'OpenStreetMap',
    tileSize: new google.maps.Size(256, 256),
    minZoom: 1,
    maxZoom: 19,
    getTileUrl: (tileCoord, zoom) => {
      const tileCount = 2 ** zoom;
      const x = ((tileCoord.x % tileCount) + tileCount) % tileCount;
      const y = tileCoord.y;
      const subdomain = ['a', 'b', 'c'][Math.abs(x + y) % 3];

      if (y < 0 || y >= tileCount) return null;

      return `https://${subdomain}.tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
    },
  });

  map.mapTypes.set(OPEN_STREET_MAP_ID, osmMapType);
  openStreetMapRegistry.set(map, osmMapType);
  map.set('openStreetMapType', osmMapType);
  map.__openStreetMapType = osmMapType;
  openStreetMapOverlayRegistry.set(map, createOpenStreetMapOverlay(google, map));

  if (showControl && !customControl) {
    const currentOptions = map.get('mapTypeControlOptions') || {};
    const mapTypeIds = Array.from(new Set([
      ...(currentOptions.mapTypeIds || ['roadmap', 'satellite']),
      OPEN_STREET_MAP_ID,
    ]));

    map.setOptions({
      mapTypeControl: true,
      mapTypeControlOptions: {
        ...currentOptions,
        mapTypeIds,
      },
    });
  }

  if (customControl) {
    map.setOptions({ mapTypeControl: false });

    const control = document.createElement('div');
    control.style.cssText = [
      'display:flex',
      'margin:10px 0 0 8px',
      'border-radius:2px',
      'box-shadow:0 1px 4px rgba(0,0,0,.3)',
      'overflow:hidden',
    ].join(';');

    const options = [
      ['roadmap', 'Mapa'],
      ['satellite', 'Satélite'],
      [OPEN_STREET_MAP_ID, 'OpenStreetMap'],
    ];

    options.forEach(([mapTypeId, label], index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.title = `Mostrar ${label}`;
      button.setAttribute('aria-label', `Mostrar ${label}`);
      button.style.cssText = [
        'height:40px',
        'padding:0 16px',
        'border:0',
        `border-left:${index === 0 ? '0' : '1px solid #ddd'}`,
        'background:#fff',
        'color:#555',
        'cursor:pointer',
        'font:14px Arial,sans-serif',
        'white-space:nowrap',
      ].join(';');
      button.addEventListener('click', () => setOpenStreetMapView(map, mapTypeId));
      control.appendChild(button);
    });

    map.controls[google.maps.ControlPosition.TOP_LEFT].push(control);

    const updateCustomControl = () => {
      Array.from(control.children).forEach((button, index) => {
        const isSelected = options[index][0] === map.getMapTypeId();
        button.style.fontWeight = isSelected ? '700' : '400';
        button.style.color = isSelected ? '#111' : '#555';
        button.style.background = isSelected ? '#f5f5f5' : '#fff';
      });
    };

    map.addListener('maptypeid_changed', updateCustomControl);
    updateCustomControl();
  }

  const attribution = document.createElement('div');
  attribution.style.cssText = [
    'display:none',
    'margin:0 5px 5px 0',
    'padding:1px 4px',
    'background:rgba(255,255,255,.86)',
    'color:#555',
    'font:10px/1.3 Arial,sans-serif',
  ].join(';');
  attribution.innerHTML = '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';
  map.controls[google.maps.ControlPosition.BOTTOM_RIGHT].push(attribution);

  const updateAttribution = () => {
    attribution.style.display = map.getMapTypeId() === OPEN_STREET_MAP_ID || map.get('openStreetMapOverlayActive') === true
      ? 'block'
      : 'none';
  };

  map.set('openStreetMapAttributionUpdater', updateAttribution);
  map.addListener('maptypeid_changed', updateAttribution);
  map.addListener('maptypeid_changed', () => {
    if (map.getMapTypeId() === OPEN_STREET_MAP_ID && map.__openStreetMapOverlayActive !== true) {
      setOpenStreetMapView(map, OPEN_STREET_MAP_ID);
    }
  });
  updateAttribution();
};

export const setOpenStreetMapView = (map, mapTypeId) => {
  if (!map) return;

  const isOpenStreetMap = mapTypeId === OPEN_STREET_MAP_ID;
  const osmMapType = openStreetMapRegistry.get(map) || map.__openStreetMapType || map.get('openStreetMapType');
  const osmOverlay = openStreetMapOverlayRegistry.get(map);
  const isOverlayActive = map.__openStreetMapOverlayActive === true;

  if (isOpenStreetMap && osmMapType) {
    // The ImageMapType must be selected as the map's actual base type. The
    // previous implementation only placed an OverlayView above Google's
    // roadmap, so switching to OpenStreetMap could leave the Google tiles
    // visible underneath (and made the option unreliable on some map IDs).
    if (map.getMapTypeId() !== OPEN_STREET_MAP_ID) {
      map.setMapTypeId(OPEN_STREET_MAP_ID);
    }

    // Keep the legacy overlay disabled. It is no longer needed when the
    // registered ImageMapType is used as the base map.
    if (isOverlayActive) {
      osmOverlay?.setMap(null);
      map.set('openStreetMapOverlayActive', false);
      map.__openStreetMapOverlayActive = false;
    }

    map.get('openStreetMapAttributionUpdater')?.();
    return;
  }

  if (!isOpenStreetMap && isOverlayActive) {
    osmOverlay?.setMap(null);
    map.set('openStreetMapOverlayActive', false);
    map.__openStreetMapOverlayActive = false;
    map.get('openStreetMapAttributionUpdater')?.();
  }

  map.setMapTypeId(mapTypeId);
};

